// @flow
import * as React from 'react'
import type { Customer } from 'types/customer'
import type { QueueItem } from 'types/queue-item'
import type { Session } from 'types/session'
import type { SessionObject } from 'types/session-object'
import { v4 as uuid } from 'uuid'

type CreateFillSessionArgs = {
  queue: Array<QueueItem>,
  type?: 'packet' | 'form' | 'document',
  isWaiting?: boolean,
  customer?: Customer,
}

type ContextTabletModeType = {
  sessions: SessionObject,
  setSessions: (SessionObject | ((SessionObject) => SessionObject)) => void,
  createFillSession: (args: CreateFillSessionArgs) => string,
}

type Props = {
  children: React.Node,
}

export const ContextTabletMode: React$Context<ContextTabletModeType> =
  React.createContext({
    sessions: {},
    setSessions: () => {},
    createFillSession: () => '',
  })

export function ContextTabletModeProvider({ children }: Props): {} {
  const currentFillSessions = window.localStorage.getItem('sessions')

  const [sessions, setSessions] = React.useState<SessionObject>(
    JSON.parse(currentFillSessions || '{}')
  )

  React.useEffect(() => {
    const sessionToStorage = Object.keys(sessions || {}).reduce(
      (acc: { [string]: any }, key: string) => {
        acc[key] = {
          ...sessions[key],
          responses: sessions[key].responses.map((response) => ({
            id: response.id,
          })),
        }

        return acc
      },
      {}
    )

    window.localStorage.setItem(
      'sessions',
      JSON.stringify(sessionToStorage || {})
    )
  }, [sessions])

  function createFillSession({
    queue,
    type,
    isWaiting,
    customer,
  }: CreateFillSessionArgs) {
    const newSession: Session = {
      id: uuid(),
      queue,
      created_at: Date.now(),
      customer: customer || null,
      isWaiting: isWaiting === true,
      isPacket: type === 'packet',
      responses: [],
      isSubmitted: false,
    }
    setSessions((previousSessions) => {
      if (!previousSessions) {
        return { [newSession.id]: newSession }
      }

      return { ...previousSessions, [newSession.id]: newSession }
    })
    return newSession.id
  }

  const contextProps = {
    sessions,
    setSessions,
    createFillSession,
  }

  return (
    <ContextTabletMode.Provider value={contextProps}>
      {children}
    </ContextTabletMode.Provider>
  )
}
